import { AbstractControl, ValidationErrors, Validators, ValidatorFn, FormGroup } from '@angular/forms';
import { extractStringValue } from '@rx-applications/shared/utils';

export const patterns = {
  /**
   * @prop name - match not present in: a-z, A-Z, ', ., -
   */
  name: /[^a-zA-Z\x7f-\xff'.\s-]/,
};

export enum MinLengths {
  firstName = 1,
  lastName = 2,
}

/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
export enum MaxLengths {
  email = 128,
  phone = 50,
  address1 = 50,
  address2 = 50,
  city = 50,
  postcode = 10,
  countrycode = 4,
}

// @dynamic
export class RxValidators {
  /**
   * @description
   * Validator that requires the control's value pass username validation test:
   * - a string username (without @) is allowed
   * - or a valid email address
   *
   * @usageNotes
   * ### Validate that the field matches a valid email pattern
   *
   * ```typescript
   * const control = new FormControl('bad@', Validators.email);
   *
   * console.log(control.errors); // {email: true}
   * ```
   *
   * @returns An error map with the `required` or `email` property
   * if the validation check fails, otherwise `null`.
   */
  static username(control: AbstractControl): ValidationErrors | null {
    return control && control.value.includes('@')
      ? Validators.required(control) || Validators.email(control)
      : Validators.required(control);
  }

  /**
   * @description
   * Validator that requires the provided properties to exist (truthy) in the control value
   *
   * @usageNotes
   * ### Validate that requiredProp2 exists in the control
   *
   * ```typescript
   * const control = new FormControl({ requiredProp1: 'prop1', requiredProp2: '' },
   *  RxValidators.propertiesRequired('requiredProp2'));
   *
   * console.log(control.errors); // {required: ['requiredProp2']}
   * ```
   *
   * @returns An error map with the `required` property
   * if the validation check fails, otherwise `null`.
   */
  static propertiesRequired(...properties: string[]) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control || !properties) {
        return null;
      }
      const object = control.value || {};
      const requiredProps = properties.filter((property: string) => !object[property]);
      return requiredProps.length === 0 ? null : { required: requiredProps };
    };
  }

  /**
   * @description
   * Validator that requires a variable amount of control names to be passed.
   * The combined length of the controls are evaluated against a limit.
   * If validation fails, the matching controls are set with the validation errors.
   *
   * @usageNotes
   * ### Validate against a maximum of 50
   *
   * ```typescript
   * const form = new FormGroup({
   *   firstName = new FormControl('Really really really really really really really'),
   *   lastName = new FormControl('long name')
   * }, RxValidators.combinedLength(50,'firstName', 'lastName'));
   *
   * console.log(form.controls['firstName'].errors); // {combinedLength: { requiredLength: 50, actualLength: 58 } }
   * console.log(form.controls['lastName'].errors); // {combinedLength: { requiredLength: 50, actualLength: 58 } }
   * ```
   * @returns An error map with the `combinedLength` property
   * if the validation check fails, otherwise `null`.
   */
  static combinedLength(limit: number, ...controlNames: string[]): ValidatorFn {
    const errorKey = 'combinedLength',
      getError = (actual: number) => {
        return { [errorKey]: { requiredLength: limit, actualLength: actual } };
      };
    return (formGroup: FormGroup): ValidationErrors | null => {
      const controls: Array<AbstractControl | null> = controlNames.map((controlName) => formGroup.get(controlName));

      if (controls.find((control) => !control)) {
        return null;
      }
      const totalLength = controls.reduce(
        (runningTotal: number, control: AbstractControl) => runningTotal + control.value.length,
        0
      );
      if (totalLength >= limit) {
        const error = getError(totalLength);
        controls.forEach((control) => addErrors(error, control));
        return error;
      } else {
        controls.forEach((control) => removeErrors([errorKey], control));
        return null;
      }
    };
  }

  /**
   * @description
   * Validator that is used for free location text filed in group booking form.
   * It checks for a-z, A-Z, 0-9, fullstops, commas, hypens, underscores, spaces.
   * At least one alphanumerics character needs to be included.
   *
   *
   * @returns An error map with the `freeLocationError` property
   * if the validation check fails, otherwise `null`.
   */
  static freeLocationValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const freelocationString = control.value;
      const containsAnyLettersOrDigits = /(([a-zA-Z0-9]+))/.test(freelocationString);
      const alphanumericsWithhypenCommaFullstop = /^(\d*\w*-*\s*,*\.*)*$/.test(freelocationString);
      return alphanumericsWithhypenCommaFullstop && containsAnyLettersOrDigits ? null : { freeLocationError: true };
    };
  }

  static comaSeparatedEmails(control: AbstractControl): ValidationErrors | null {
    // This regex checks for valid email addresses separated by comma. It allows for trailing comma and a space between the mail and comma.
    const EMAIL_REGEXP =
      /^(?=.{1,254}$)(?=.{1,64}@)([a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*)(?:\s*,\s*([a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*))*$/;

    if (control.value == null || control.value.length === 0) {
      return null; // don't validate empty values to allow optional controls
    }
    return EMAIL_REGEXP.test(control.value) ? null : { emailSeparatedByComma: true };
  }

  static phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const phoneNumber = control.value?.replace(/\D/g, '');
      const isValid = /^\d{7,20}$/.test(phoneNumber);
      return isValid ? null : { pattern: true };
    };
  }

  /**
   * @description
   * Validator that disallows input based on pattern
   *
   * @usageNotes
   * ### Validate that the input is not allowed
   *
   * ```typescript
   * const control = new FormControl('test%88$', [PatternValidators.matches(/[^a-zA-Z\x7f-\xff'\s-]/)]);
   *
   * console.log(control.errors); // {patternMatches: ['%','8','$']}
   * ```
   *
   * @param pattern regex pattern to disallow
   * @param autoCompleteKey optional key to find when used with autocomplete
   * @returns An error map with the `patternMatches` property with a distinct array value of matches
   * if the validation check fails, otherwise `null`.
   */
  static matches(pattern: RegExp, autoCompleteKey?: string): ValidatorFn {
    return (control: AbstractControl) => {
      const globalPattern = new RegExp(pattern, 'g');
      const value = control.value;
      const stringValue = extractStringValue(value, autoCompleteKey);
      const patternMatches = [...new Set(stringValue?.match(globalPattern))];

      return patternMatches.length ? { patternMatches } : null;
    };
  }

  /**
   * @description
   * Validator the checks if a dependent control is required based on the value of the control
   */
  static dependentRequiredValidator(dependentControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const dependentControl = control.parent?.get(dependentControlName);

      if (!!control.value && !dependentControl?.value) {
        dependentControl?.setErrors({ dependentRequired: true });
      } else if (!control.value && dependentControl?.value) {
        return { dependentRequired: true };
      } else {
        dependentControl?.setErrors(null);
      }

      return null;
    };
  }
}

function addErrors(errors: ValidationErrors, control: AbstractControl) {
  control.setErrors({ ...control.errors, ...errors });
}

function removeErrors(errorKeys: string[], control: AbstractControl) {
  const remainingErrors = errorKeys.reduce(
    (errors, key) => {
      delete errors[key];
      return errors;
    },
    { ...control.errors }
  );
  control.setErrors(Object.keys(remainingErrors).length > 0 ? remainingErrors : null);
}
